import type { ReactNode, Ref } from "react";
import type { LabelProps as AriaLabelProps } from "react-aria-components";

import { HelpCircle } from "@untitledui/icons";
import { Label as AriaLabel } from "react-aria-components";

import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip/tooltip";
import { cx } from "@/utils/cx";

interface LabelProps extends AriaLabelProps {
	children: ReactNode;
	isRequired?: boolean;
	ref?: Ref<HTMLLabelElement>;
	tooltip?: string;
	tooltipDescription?: string;
}

export const Label = ({
	className,
	isRequired,
	tooltip,
	tooltipDescription,
	...props
}: LabelProps) => {
	const requiredIndicator = isRequired ? (
		<span aria-hidden="true" className="text-error-primary">
			*
		</span>
	) : null;

	const label = (
		<AriaLabel
			// Used for conditionally hiding/showing the label element via CSS:
			// <Input label="Visible only on mobile" className="lg:**:data-label:hidden" />
			// or
			// <Input label="Visible only on mobile" className="lg:label:hidden" />
			data-label="true"
			{...props}
			className={cx(
				"flex cursor-default items-center gap-0.5 text-sm font-medium text-secondary",
				!tooltip && className,
			)}
		>
			{props.children}
			{requiredIndicator}
		</AriaLabel>
	);

	if (!tooltip) {
		return label;
	}

	// Keep the help button outside <label>. A <button> inside <label> is invalid
	// HTML; browsers hoist it, which can collapse parent layout when a field
	// with a tooltip mounts/unmounts (notably Select).
	return (
		<div
			className={cx("flex items-center gap-0.5", className)}
			data-label="true"
		>
			<AriaLabel
				{...props}
				className="flex cursor-default items-center gap-0.5 text-sm font-medium text-secondary"
			>
				{props.children}
				{requiredIndicator}
			</AriaLabel>

			<Tooltip description={tooltipDescription} placement="top" title={tooltip}>
				<TooltipTrigger
					className="cursor-pointer text-fg-quaternary transition duration-200 hover:text-fg-quaternary_hover focus:text-fg-quaternary_hover"
					// `TooltipTrigger` inherits the disabled state from the parent form field
					// but we don't that. We want the tooltip be enabled even if the parent
					// field is disabled.
					isDisabled={false}
				>
					<HelpCircle className="size-4" />
				</TooltipTrigger>
			</Tooltip>
		</div>
	);
};

Label.displayName = "Label";
